<template>
    <div class="inboxView overview" :class="{loading: inboxStore.loading, event_triggered: inboxactionStore.triggered}">
        <div class="Heading flex">
            <div class="left">
                <div class="element SwitchField">
                    <label>
                        <div class="switch">
                            <input type="checkbox" @change.prevent="toggleSendItems">
                            <span class="slider round"></span>
                        </div>
                        <span class="title">{{ trans("Toon verzonden items") }}</span>
                    </label>
                </div>
            </div>
            <div class="right"></div>
        </div>
        <div class="centered" v-if="inboxStore.loading && !routeData.item_id">
            {{ trans("Aan het laden...") }}
        </div>
        <div class="centered" v-else-if="!inboxStore.loading && !routeData.item_id && !items.length">
            {{ trans("Geen items gevonden.")}}
        </div>
        <div class="inner" v-else>
            <ul class="block-grid one-up list">
                <li v-for="item in items">
                    <div v-if="(`${routeData.item_id}` !== `${item.data.id}`) && inboxStore.current" class="inboxItem tabular" :class="{ unread: !read(item) }">
                        <div class="td td_sender" @click.prevent="handleItemClick(item)">
                            <div class="sender_name" :title="item.data.sender_name">{{ contact_name(item) }}</div>
                            <div class="sender_uid" :title="item.data.sender_uid">{{ contact_uid(item) }}</div>
                        </div>
                        <div class="td td_message" @click.prevent="handleItemClick(item)">
                            <div class="subject">
                                <Icon v-if="attachments(item).length" name="paperclip" :title="trans('Bevat %s bijlagen',  attachments(item).length)" />
                                <span class="title">{{ item.data.subject }}</span>
                            </div>
                            <div class="tags">
                                <InboxTags :item="item" />
                            </div>
                        </div>
                        <div class="td td_date" :set="dateObject(item)" @click.prevent="handleItemClick(item)">
                            <template v-if="((new Date() - dateObject(item)) > twoWeeks)">
                                <div class="receive_date" :title="formatDateTime(dateObject(item))">{{ formatDate(dateObject(item)) }}</div>
                                <div class="receive_date" :title="formatDateTime(dateObject(item))"><small>{{ formatTime(dateObject(item)) }} {{ trans("uur") }}</small></div>
                            </template>
                            <template v-else-if="date">
                                <div class="receive_date" :title="formatDateTime(dateObject(item))"><timeago :datetime="dateObject(item)" /></div>
                            </template>
                        </div>
                        <!--
                        <div class="td td_actions">
                            <template v-if="!isSent(item)">

                            </template>
                        </div>
                        -->
                    </div>
                    <div class="inboxView detail" v-if="`${routeData.item_id}` === `${item.data.id}`">
                        <InboxDetail :item="inboxStore.current" />
                        <InboxMessagearea :item="inboxStore.current" />
                    </div>
                </li>
            </ul>
        </div>
        <div class="Heading flex noborder">
            <div class="left"></div>
            <div class="right">
                <PaginationSimple
                    :class="{ hidden: inboxStore.total <= routeData.limit }"
                    pagination_key="page"
                    :id="null"
                    :pagination_limit="routeData.limit"
                    :pagination_total="inboxStore.total"
                    :pagination_current="inboxStore.page"
                    @updatePage="handlePagination"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, reactive, ref, onMounted, onUnmounted, computed, watch } from "vue";
import {useRoute, useRouter} from "vue-router";

// Helper functions
import {apiHostname} from "config";
import { getDataAsync, patchDataAsync, fetchDataAsync } from "helpers/_api";
import { resolveData } from "helpers/_data";
import { getQuery, getUrlWithQueryItemReplaced } from "helpers/_structure";
import trans, { locale } from "helpers/_translation";

// Our components
// import inlineOverview from "components/notification-overview";
import Icon from "components/icon";
import PaginationSimple from "components/pagination-simple";

import InboxDetail from "view/inbox/includes/inbox-detail";
import InboxActions from "view/inbox/includes/inbox-actions";
import InboxTags from "view/inbox/includes/inbox-tags";
import InboxMessagearea from "view/inbox/includes/inbox-messagearea";

// Config for API urls used in this vue template
import config from "config";
import {
    routeData,
    isSent,
    source,
    read,
    favorite,
    important,
    date,
    contact_name,
    contact_uid,
    avatar,
    attachments,
    html,
    iframe,
    adjustIframeHeight,

    formatDate,
    formatDateTime,
    formatTime,
    twoWeeks,
    selected,
    selectedAll,
    handleSelect,
    handleSelectAll,

    handleMounted,
    handleUnmounted
} from "helpers/_inbox-helpers";
import { inboxStore, resetInbox, getInbox, updateCount, updateAllCount } from "store/inbox-store";
import { inboxactionStore } from "store/inboxaction-store";

export default defineComponent({
    name: "inbox-overview",
    components: {
        Icon,
        PaginationSimple,
        InboxDetail,
        InboxActions,
        InboxTags,
        InboxMessagearea
    },
    props: {
        item_id: {
            type: String,
            required: false
        },
        module_object: {
            type: String,
            required: false
        }
    },
    setup(props) {

        const router = useRouter();
        const route = useRoute();

        const searchInput = ref(getQuery("search") ?? "");
        const handleSearch = () => {
            Object.assign(routeData, {
                page: 1,
                search: searchInput.value
            });
        };

        const componentData = reactive({
            showSendItems: false,
            sendItems: []
        });

        // Set routeData for module based on detail page
        watch(
            () => [props.item_id, props.module_object],
            () => {
                const tmpObject = {
                    module_item_id: props.item_id,
                    module_object: props.module_object
                };
                Object.assign(routeData, tmpObject);
            },
            {
                immediate: true,
                deep: true
            }
        );
        onUnmounted(() => {
            const tmpObject = {
                module_item_id: null,
                module_object: null
            };
            Object.assign(routeData, tmpObject);
        });

        watch(
            () => route.params,
            (newData, oldData) => {
                resetInbox();

                const newObject = {
                    module: "Inbox",
                    action: "related",
                    inbox_id: route.params.inbox_id ?? null,
                    item_id: route.params.item_id ?? null
                };
                if (oldData && !newData.item_id) {
                    // Only reset page when going to a item detail
                    // Allows us to return to previous page without issue.
                    // Only reset page when not navigating from the item
                    if (!oldData.item_id) {
                        newObject.page = newData.page ?? getQuery("page") ?? 1;
                    }

                    // Only reset search if not the first time
                    // And we are going to the first page
                    if (routeData.page === 1) {
                        searchInput.value = "";
                        newObject.search = "";
                    }
                }
                Object.assign(routeData, newObject);
                if (routeData.item_id) {
                    // Set selected item by default so it can be used for actions
                    inboxStore.selected = [`${routeData.item_id}`];
                }
            },
            {
                immediate: true
            }
        );
        watch(
            () => routeData,
            (newData, oldData) => {
                getInbox(routeData);
            },
            {
                immediate: true,
                deep: true
            }
        );

        const scrollToTop = () => {
            setTimeout(() => {
                const topcontainer = document.querySelector("#topcontainer");
                topcontainer && window.scrollTo({
                    behavior: 'smooth',
                    top: topcontainer.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 30
                });
            }, 200);
        };
        const handlePagination = (page, force = false) => {
            if (`${routeData.page}` === `${page}` && !force) return;
            inboxStore.selected = [];
            routeData.page = page;
            scrollToTop();
        };

        onMounted(() => {
            handleMounted();
        });
        onUnmounted(() => {
            handleUnmounted();
        });


        const handleItemBack = () => {

        };
        const handleItemClick = (item) => {
            if (!item?.data?.id) return;
            // Start clean
            inboxStore.current = null;
            // Toggle if needed
            if (`${routeData.item_id}` === `${item.data.id}`) {
                routeData.item_id = null;
                return;
            }

            const wasUnread = !read(item);
            // Set new item_id, based on selection
            routeData.item_type = item.data?.module_object ? "sent" : "inbox";
            routeData.item_id = item.data.id;
            console.log("routeData.item_type",routeData.item_type);
            // Recalculated the ones needed
            wasUnread && updateCount(routeData);
        };

        const fetchSendItems = () => {
            const filters = [
                [
                    "FieldFilter",
                    {
                        "v": "Workflowcard",
                        "c": "module_object"
                    }
                ],
                [
                    "FieldFilter",
                    {
                        "v": props.item_id,
                        "c": "item_id"
                    }
                ]
            ];
            getDataAsync(`${apiHostname}/api/v1.0/emailsent`,filters).then(result => {
                componentData.sendItems = result.data;
            }).catch((error) => {
                // Check if the error response is 403
                if (error.status === 403) {
                    componentData.sendItems = [];
                    return;
                }
                throw error;
            });
        };

        const toggleSendItems = () => {
            componentData.showSendItems = !componentData.showSendItems;
        };
        const items = computed(() => {
            return componentData.showSendItems ? componentData.sendItems : inboxStore.items;
        });

        onMounted(() => {
            fetchSendItems();
        });

        const dateObject = (item) => {
            return computed(() => {
                return new Date(date(item))
            }).value;
        };

        return {
            trans,
            searchInput,
            componentData,

            avatar,
            attachments,

            items,

            routeData,
            inboxStore,
            inboxactionStore,

            handlePagination,
            scrollToTop,

            twoWeeks,
            formatDate,
            formatDateTime,
            formatTime,

            selected,
            selectedAll,
            handleSelect,
            handleSelectAll,

            toggleSendItems,
            handleSearch,

            isSent,
            read,
            favorite,
            important,
            date,
            dateObject,
            contact_name,
            contact_uid,

            handleItemBack,
            handleItemClick
        };
    },
});
</script>